'use client';

import Tooltip from '@/components/next-ui/Tooltip';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@ui/Button';
import Input from '@ui/Input';
import Typography from '@ui/Typography';
import { isBefore, subYears } from 'date-fns';
import { defaults, pick, remove, sortBy } from 'lodash-es';
import { InfoIcon } from 'lucide-react';
import React, { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import Select from '@ui/Select';
import Checkbox from '@/components/next-ui/Checkbox';
import MonthSelector from '../CompanyOnBoard/MonthSelector';
import YearSelector from '../CompanyOnBoard/YearSelector';
import { validateEmail } from '@/utils/stringHelpers';
import { getAllISOCodes } from 'iso-country-currency';

const schema = yup.object().shape({
  email: yup
    .string()
    .email('You must enter a valid email')
    .required('You must enter an email')
    .max(50, 'Email must be at most 50 characters long'),
  // .test(
  //   'email-availability',
  //   'An account with this email already exists. Please use a different email.',
  //   async (value) => {
  //     if (!value) return true; // Skip validation if the email field is empty

  //     const isEmailAvailable = await validateEmail(value);
  //     return !isEmailAvailable;
  //   },
  // ),
  firstName: yup
    .string()
    .required('First Name is required')
    .min(2, 'First Name is too short')
    .max(20, 'First Name must be at most 20 characters long'),
  lastName: yup
    .string()
    .required('Last Name is required')
    .min(2, 'Last Name is too short')
    .max(20, 'Last Name must be at most 20 characters long'),
  jobTitle: yup
    .string()
    .required('Job Title is required')
    .max(50, 'Job Title must be at most 50 characters long'),
  company: yup
    .string()
    .required('Company is required')
    .min(2, 'Company is too short')
    .max(50, 'Company must be at most 50 characters long'),
  // isOver18: yup.boolean().isTrue('You must be over 18 years old'),
  birthMonth: yup
    .object()
    .required('Month of birth is required')
    .typeError('Month of birth is required'),
  birthYear: yup
    .object()
    .required('Year of birth is required')
    .typeError('Year of birth is required'),
  // agreeToTerms: yup.boolean().isTrue('You must agree to the terms of service'),
  country: yup
    .object()
    .required('Please select a country')
    .typeError('Please select a country'),
  zipcode: yup
    .string()
    .required('Zipcode is required')
    .min(5, 'Zipcode must be at least 5 characters')
    .max(10, 'Zipcode must be at most 10 characters'),
  agreeToTerms: yup
    .boolean()
    .isTrue('Please select the checkbox to accept the terms of service'),
});

const getCountries = () => {
  const countries = sortBy(
    getAllISOCodes().map((country) => ({
      label: country.countryName,
      id: country.iso,
      currency: country.currency,
      currencySymbol: country.symbol,
    })),
    'label',
  );

  const priorityCountries = remove(countries, (country) =>
    ['US', 'IN', 'MX'].includes(country.id),
  );

  return [
    priorityCountries.find((c) => c.id === 'US'),
    priorityCountries.find((c) => c.id === 'IN'),
    priorityCountries.find((c) => c.id === 'MX'),
    ...countries,
  ];
};

function JoinFormProfileStep(props) {
  const { nonProfit, onSubmit, formData, handleFormData } = props;

  const countries = getCountries();

  const form = useForm({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      jobTitle: '',
      company: '',
      birthMonth: null,
      birthYear: null,
      agreeToTerms: false,
      country: null,
      zipcode: '',
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    getValues,
    reset,
  } = form;

  // Update the form when formData changes
  useEffect(() => {
    const newDefaultValues = defaults(
      pick(formData, [
        'email',
        'firstName',
        'lastName',
        'jobTitle',
        'company',
        'birthMonth',
        'birthYear',
        'agreeToTerms',
        'country',
        'zipcode',
      ]),
      {
        email: '',
        firstName: '',
        lastName: '',
        jobTitle: '',
        company: '',
        birthMonth: null,
        birthYear: null,
        agreeToTerms: false,
        country: null,
        zipcode: '',
      },
    );

    reset(newDefaultValues);
  }, [formData, reset]);

  const handleFormSubmit = async (data) => {
    const { birthMonth, birthYear } = data;
    const month = birthMonth?.value;
    const year = birthYear?.value;
    const birthDate = new Date(year, month, 1);

    const isOver18 = isBefore(birthDate, subYears(new Date(), 18));
    if (!isOver18) {
      setError('birthYear', {
        type: 'manual',
        message: 'You must be at least 18 years old',
      });
    } else {
      onSubmit(data);
    }
  };

  const formValues = useWatch({ control });

  useEffect(() => {
    handleFormData((prevFormData) => {
      const currentFormData = {
        ...prevFormData,
        first_name: formValues.firstName,
        last_name: formValues.lastName,
        fullName: `${formValues.firstName} ${formValues.lastName}`,
        designation: formValues.jobTitle,
        companyName: formValues.company,
        country: formValues?.country?.id,
      };

      return currentFormData;
    });
  }, [formValues, handleFormData]);

  return (
    <div className="flex h-full w-full flex-1 flex-col gap-6">
      {/* <Typography variant="h1">
        Join {nonProfit?.name || 'Nonprofit'} community!
      </Typography> */}
      <Typography variant="h3">Let&apos;s setup your Neki profile</Typography>
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex h-full w-full flex-col">
        <div className="flex w-full flex-1 flex-col gap-4 overflow-y-auto pb-4">
          <Controller
            name="email"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Input
                label="EMAIL"
                placeholder="Email"
                required
                // disabled
                {...rest}
                error={errors.email?.message}
              />
            )}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="firstName"
              control={control}
              render={({ field: { ref, ...rest } }) => (
                <Input
                  label="FIRST NAME"
                  placeholder="First Name"
                  required
                  {...rest}
                  error={errors.firstName?.message}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field: { ref, ...rest } }) => (
                <Input
                  label="LAST NAME"
                  placeholder="Last Name"
                  required
                  {...rest}
                  error={errors.lastName?.message}
                />
              )}
            />
          </div>

          {/* <Controller
            name="jobTitle"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Input
                label="JOB TITLE"
                placeholder="Job Title"
                required
                {...rest}
                error={errors.jobTitle?.message}
              />
            )}
          /> */}

          <div className="flex gap-4">
            <div className="flex-1">
              <Controller
                name="jobTitle"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Input
                    label="WHAT'S YOUR ROLE?"
                    placeholder="Job Title"
                    required
                    {...rest}
                    error={errors.jobTitle?.message}
                  />
                )}
              />
            </div>
            <div className="flex-1">
              <Controller
                name="company"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Input
                    label="WHERE DO YOU WORK?"
                    placeholder="Company"
                    required
                    {...rest}
                    error={errors.company?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <Controller
                name="country"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Select
                    label="COUNTRY"
                    placeholder="Country"
                    required
                    options={countries}
                    {...rest}
                    error={errors.country?.message}
                  />
                )}
              />
            </div>
            <div className="flex-1">
              <Controller
                name="zipcode"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Input
                    label="ZIPCODE"
                    placeholder="Zipcode"
                    required
                    {...rest}
                    error={errors.zipcode?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <div className="mb-3 flex items-center justify-start gap-2">
                <Typography
                  component="div"
                  variant="body2"
                  className="uppercase">
                  Confirm that you are over 18 years of age.
                </Typography>
                <Tooltip
                  content={
                    <Typography
                      variant="body2"
                      className="break-words text-white">
                      Please provide your year and month of birth to verify
                      you&apos;re over 18 years old.
                    </Typography>
                  }
                  className="w-fit min-w-[200px]">
                  <InfoIcon className="h-4 w-4" />
                </Tooltip>
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Controller
                    name="birthMonth"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <MonthSelector
                        {...rest}
                        error={errors.birthMonth?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex-1">
                  <Controller
                    name="birthYear"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <YearSelector
                        {...rest}
                        error={errors.birthYear?.message}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Controller
                name="agreeToTerms"
                control={control}
                render={({ field: { onChange } }) => (
                  <Checkbox
                    onChange={onChange}
                    className="border-text-disabled font-extrabold"
                  />
                )}
              />

              <Typography variant="body2" component="div">
                I have read and agree to the
                <Typography
                  target="_blank"
                  component="a"
                  variant="body2"
                  href="https://www.neki.io/terms-and-conditions"
                  className="mx-0.5 underline hover:text-text-hyperlink">
                  terms of service
                </Typography>
              </Typography>
            </div>
            {errors.agreeToTerms?.message && (
              <Typography variant="caption" color="error">
                {errors.agreeToTerms?.message}
              </Typography>
            )}
          </div>
        </div>

        <div className="flex w-full justify-end pt-3">
          <Button variant="filled" color="primary" type="submit">
            Submit
          </Button>
        </div>
      </form>
    </div>
  );
}

export default JoinFormProfileStep;
